// Copyright © 2019-2026 Focal Systems. All rights reserved.

import { useQuery } from "@tanstack/react-query";
import { api } from "@/api/client";
import type { components } from "@/api/schema";

export type HomeStoreEvent = components["schemas"]["HomeStoreEventResponse"];
export type HomeStoreEvents = components["schemas"]["HomeStoreEventsResponse"];
export type HomeWhatsHappening = components["schemas"]["HomeWhatsHappeningResponse"];
export type HomeMetrics = components["schemas"]["HomeMetricsResponse"];

/**
 * Stores installing and going live around today. Open to every signed-in user — deliberately not
 * routed through `/v1/stores`, which needs the stores view permission. The server owns the date
 * windows and counts days against each store's own local today.
 */
export function useWhatsHappeningQuery() {
  return useQuery({
    queryKey: ["home", "whats-happening"],
    queryFn: async () => {
      const res = await api.GET("/v1/home/whats-happening");
      // An error status with an empty body leaves both `error` and `data` undefined.
      if (res.error || !res.data) throw new Error("Failed to load what's happening");
      return res.data as HomeWhatsHappening;
    },
  });
}

/** Headline counts: active stores, devices and users (each as its list page shows by default), and today's expected images. */
export function useHomeMetricsQuery() {
  return useQuery({
    queryKey: ["home", "metrics"],
    queryFn: async () => {
      const res = await api.GET("/v1/home/metrics");
      if (res.error || !res.data) throw new Error("Failed to load home metrics");
      return res.data as HomeMetrics;
    },
  });
}
